import React, { useCallback, useEffect, useState } from 'react';
import { devicesApi, DeviceHealth, DevicesResponse } from '../services/api';
import { Button } from '../ds/components/Button';
import { StatusChip, InspectionStatus } from '../ds/components/StatusChip';
import { formatDate } from '../utils/dateFormat';

/**
 * „Tablety" (Ustawienia) — stan zdrowia urządzeń kominiarzy (2026-10-09, migr. 061).
 *
 * Po co: tablet trzymał rekord z 409 przez 15 dni i nikt nie wiedział, dopóki kierownik terenu
 * nie przysłał zdjęć ekranu. Tablet raportuje po każdym przebiegu synchronizacji; tu widać kto,
 * jaka wersja, ile czeka/odrzucone i od kiedy. Problemy najpierw (kolejność liczy backend).
 *
 * Czytanie: „Czeka" schodzi samo po powrocie sieci; „Odrzucone"/„Konflikty" NIE zejdą same —
 * kominiarz musi otworzyć „Sync" na tablecie. „nieaktualna" = tablet nie odświeżył aplikacji;
 * „poniżej …" = przy włączonej blokadzie starych wersji jego zapisy dostają 426. Wiersz „Stara aplikacja"
 * to NIE urządzenie, tylko zapis ze starszej wersji (bez raportów stanu) na danym koncie i w danej
 * przeglądarce — liczniki nieznane („—"). Po 7 dniach bez takiego zapisu przestaje być problemem.
 */

const cell: React.CSSProperties = {
  padding: 'var(--space-3) var(--space-4)',
  borderBottom: 'var(--border-width) solid var(--border)',
  fontSize: 'var(--text-label)',
  color: 'var(--text-primary)',
  verticalAlign: 'top',
};

const head: React.CSSProperties = {
  ...cell,
  background: 'var(--surface-fill)',
  fontWeight: 'var(--weight-semibold)',
  color: 'var(--text-secondary)',
  textAlign: 'left',
  whiteSpace: 'nowrap',
};

const num: React.CSSProperties = { ...cell, textAlign: 'right', fontVariantNumeric: 'tabular-nums' };
const muted: React.CSSProperties = { color: 'var(--text-secondary)', fontSize: 'var(--text-caption)' };

/** „12 min", „3 h", „15 dni" — wiek znacznika czasu. */
function ageLabel(iso: string | null, now: number = Date.now()): string | null {
  if (!iso) return null;
  const t = Date.parse(iso);
  if (!Number.isFinite(t)) return null;
  const min = Math.max(0, Math.round((now - t) / 60000));
  if (min < 60) return `${Math.max(1, min)} min`;
  const h = Math.round(min / 60);
  if (h < 48) return `${h} h`;
  return `${Math.floor(min / 1440)} dni`;
}

/** „Android 14 · Chrome 129" — skrót user-agenta (pełny w podpowiedzi). */
function browserLabel(ua: string | null): string {
  if (!ua) return 'nieznana przeglądarka';
  // Kolejność ma znaczenie: UA Androida zawiera też „Linux".
  const os = [/Android [\d.]+/, /iPad|iPhone/, /Windows NT [\d.]+/, /Mac OS X [\d_]+/, /CrOS/, /Linux/]
    .map((re) => re.exec(ua)?.[0])
    .find(Boolean)
    ?.replace(/_/g, '.');
  const br = /(Edg|SamsungBrowser|Firefox|Chrome|Version)\/(\d+)/.exec(ua);
  const name = br ? `${br[1] === 'Version' ? 'Safari' : br[1] === 'Edg' ? 'Edge' : br[1]} ${br[2]}` : null;
  return [os, name].filter(Boolean).join(' · ') || ua.slice(0, 40);
}

/**
 * Komunikat błędu pobrania listy po polsku. Błąd sieci przeglądarki (TypeError „Failed to fetch" w Chrome,
 * „NetworkError…" w Firefoksie, „Load failed" w Safari) to brak połączenia, nie treść z serwera.
 */
function loadErrorMessage(e: unknown): string {
  const msg = e instanceof Error ? e.message : '';
  if (e instanceof TypeError || /failed to fetch|networkerror|load failed/i.test(msg)) {
    return 'Brak połączenia z serwerem — sprawdź sieć i naciśnij „Odśwież”.';
  }
  return msg || 'Nie udało się pobrać listy tabletów.';
}

/** Najstarszy niewysłany rekord: oczekujący albo odrzucony — co starsze. */
function oldestUnsent(d: DeviceHealth): string | null {
  const ts = [d.oldestPendingAt, d.oldestFailedAt].filter((v): v is string => !!v);
  if (ts.length === 0) return null;
  return ts.reduce((a, b) => (Date.parse(a) <= Date.parse(b) ? a : b));
}

/** Chipy stanu — ikona + kolor + tekst (nigdy sam kolor). */
function stateChips(d: DeviceHealth): { status: InspectionStatus; label: string }[] {
  const chips: { status: InspectionStatus; label: string }[] = [];
  if (d.legacy) chips.push({ status: 'todo', label: 'Bez raportów — liczniki nieznane' });
  if (d.flags.stuck) {
    chips.push({ status: 'deny', label: d.conflicts > 0 ? 'Konflikt — rozwiąż na tablecie' : 'Odrzucone przez serwer' });
  }
  if (d.flags.staleQueue) chips.push({ status: 'rec', label: 'Kolejka stoi' });
  if (d.flags.silent) chips.push({ status: 'todo', label: 'Milczy od 3 dni' });
  if (chips.length === 0 && !d.problem) chips.push({ status: 'ok', label: 'OK' });
  return chips;
}

export const DevicesPanel: React.FC = () => {
  const [data, setData] = useState<DevicesResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setData(await devicesApi.list());
    } catch (e) {
      setError(loadErrorMessage(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const devices = data ? [...data.devices].sort((a, b) => Number(b.problem) - Number(a.problem)) : [];
  const problems = devices.filter((d) => d.problem).length;

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 'var(--space-4)', flexWrap: 'wrap' }}>
        <p style={{ margin: 0, color: 'var(--text-secondary)' }}>
          {data ? (
            <>
              Aktualna wersja: <strong>{data.currentVersion || 'nieznana'}</strong>
              {' · '}Blokada starych wersji: <strong>{data.minVersion ? `od ${data.minVersion}` : 'wyłączona'}</strong>
              {devices.length > 0 && <>{' · '}Z problemem: <strong>{problems}</strong> z {devices.length}</>}
            </>
          ) : 'Stan tabletów kominiarzy'}
        </p>
        <Button variant="secondary" icon="refresh" onClick={load} disabled={loading}>
          {loading ? 'Odświeżanie…' : 'Odśwież'}
        </Button>
      </div>

      {error && (
        <p role="alert" style={{ color: 'var(--status-deny)', marginTop: 'var(--space-4)' }}>
          {error}
        </p>
      )}

      {!loading && !error && devices.length === 0 && (
        <p style={{ color: 'var(--text-secondary)', marginTop: 'var(--space-4)' }}>
          Żaden tablet jeszcze się nie zgłosił. Tablety z aktualną aplikacją raportują stan same; starsze
          pojawią się tu po pierwszym zapisie lokalu albo źródeł ciepła (jako „Stara aplikacja").
        </p>
      )}

      {devices.length > 0 && (
        <div style={{ overflowX: 'auto', marginTop: 'var(--space-4)' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', background: 'var(--surface-card)' }}>
            <thead>
              <tr>
                <th style={head}>Użytkownik</th>
                <th style={head}>Urządzenie</th>
                <th style={head}>Wersja</th>
                <th style={head}>Ostatnio widziany</th>
                <th style={{ ...head, textAlign: 'right' }}>Czeka</th>
                <th style={{ ...head, textAlign: 'right' }}>Konflikty</th>
                <th style={{ ...head, textAlign: 'right' }}>Odrzucone</th>
                <th style={{ ...head, textAlign: 'right' }}>Nie do wysłania</th>
                <th style={head}>Najstarszy niewysłany</th>
                <th style={head}>Stan</th>
              </tr>
            </thead>
            <tbody>
              {devices.map((d) => {
                const oldest = oldestUnsent(d);
                return (
                  <tr
                    key={d.deviceId}
                    data-testid={`device-${d.shortId}`}
                    style={{ boxShadow: d.problem ? 'inset 6px 0 0 var(--status-deny-bar)' : undefined }}
                  >
                    <td style={cell}>
                      <div style={{ fontWeight: 'var(--weight-semibold)' }}>{d.userName || d.userEmail || 'nieznany'}</div>
                      {d.userName && d.userEmail && <div style={muted}>{d.userEmail}</div>}
                    </td>
                    <td style={cell}>
                      {d.legacy ? (
                        <>
                          <div style={{ fontWeight: 'var(--weight-semibold)' }}>Stara aplikacja</div>
                          <div style={muted} title={d.userAgent || undefined}>
                            przeglądarka: {browserLabel(d.userAgent)}
                          </div>
                        </>
                      ) : (
                        <code title={d.userAgent || undefined}>{d.shortId}</code>
                      )}
                      {d.online === false && <div style={muted}>offline przy raporcie</div>}
                    </td>
                    <td style={cell}>
                      <div style={{ whiteSpace: 'nowrap' }}>{d.legacy ? 'starsza, bez raportów' : d.appVersion || '—'}</div>
                      {d.flags.belowMin && data?.minVersion ? (
                        <StatusChip status="deny" label={`poniżej ${data.minVersion} — zapisy blokowane`} size="sm" />
                      ) : d.flags.outdated ? (
                        <StatusChip status="rec" label="nieaktualna" size="sm" />
                      ) : null}
                    </td>
                    <td style={cell} title={formatDate(d.lastSeenAt)}>
                      {ageLabel(d.lastSeenAt)} temu
                      <div style={muted}>{d.legacy ? 'ostatni zapis' : 'ostatni raport'}</div>
                    </td>
                    <td style={num}>{d.legacy ? '—' : d.pending}</td>
                    <td style={{ ...num, color: d.conflicts > 0 ? 'var(--status-deny)' : undefined }}>{d.legacy ? '—' : d.conflicts}</td>
                    <td style={{ ...num, color: d.failed > 0 ? 'var(--status-deny)' : undefined }}>{d.legacy ? '—' : d.failed}</td>
                    <td style={num}>{d.legacy ? '—' : d.blocked}</td>
                    <td style={cell}>{oldest ? `${ageLabel(oldest)} temu` : '—'}</td>
                    <td style={cell}>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--space-2)' }}>
                        {stateChips(d).map((c) => <StatusChip key={c.label} status={c.status} label={c.label} size="sm" />)}
                      </div>
                      {d.lastError && <div style={{ ...muted, marginTop: 'var(--space-2)', maxWidth: 320 }}>Ostatni błąd: {d.lastError}</div>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

